import React, { useMemo } from 'react';
import { graphNodeKey } from 'DataGraph/dataGraphApi';
import { objectKindLabel, objectKindOf } from 'DataGraph/objectKinds';
import { formatRecords, formatStorage } from './format';

export const FILTERS = [['all', 'All'], ['connected', 'Connected'], ['isolated', 'Isolated'], ['selected', 'Selected']];

const COLUMNS = [
  ['name', 'Object', false],
  ['custom', 'Type', false],
  ['rows', 'Records', true],
  ['size', 'Est. storage', true],
  ['out', 'Lookups out', true],
  ['in', 'Lookups in', true],
  ['prefix', 'Key prefix', false],
];

const attr = (n, k) => (n && n.attributes ? n.attributes[k] : undefined);
const valueOf = (n, key) => {
  switch (key) {
    case 'name': return n.table;
    case 'custom': return attr(n, 'custom') === true ? 1 : 0;
    case 'rows': return n.rowCount == null ? null : Number(n.rowCount);
    case 'size': return n.sizeBytes == null ? null : Number(n.sizeBytes);
    case 'out': return n.edgeCount ? n.edgeCount.out || 0 : 0;
    case 'in': return n.edgeCount ? n.edgeCount.in || 0 : 0;
    case 'prefix': return attr(n, 'keyPrefix') || '';
    default: return null;
  }
};

/** Client-side search / filter / sort over the full in-memory node set. Nulls sort last either way. */
export function selectObjects(items, { query = '', filter = 'all', sort = { key: 'rows', dir: 'desc' }, picked = null } = {}) {
  const q = String(query || '').trim().toLowerCase();
  let out = (items || []).filter((n) => {
    if (filter === 'connected' && n.isolated) return false;
    if (filter === 'isolated' && !n.isolated) return false;
    if (filter === 'selected' && !(picked && picked.has(n.table))) return false;
    if (!q) return true;
    return [n.table, attr(n, 'label'), attr(n, 'keyPrefix')].some((s) => s && String(s).toLowerCase().includes(q));
  });
  const { key, dir } = sort || {};
  const sign = dir === 'desc' ? -1 : 1;
  out = out.slice().sort((a, b) => {
    const va = valueOf(a, key); const vb = valueOf(b, key);
    if (va == null && vb == null) return String(a.table).localeCompare(String(b.table));
    if (va == null) return 1;
    if (vb == null) return -1;
    const c = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return (c !== 0 ? c * sign : String(a.table).localeCompare(String(b.table)));
  });
  return out;
}

/**
 * `picked` / `onPick` / `onPickAll` add the choice column: which objects the owner wants
 * discovery to sample and the TDM plan to stay inside (saved as config.selectedObjects). Without
 * `picked` there is no choice column. Row click opens the object (`onSelect`); the checkbox is
 * the choice.
 */
// `rowFilters` ({ lowercased object: expression }, Data Graph only) adds a Row filter column; `planTags`
// ({ lowercased object: { text, tone, title } }, Data Graph only) tags what the graph's list saves or needs.
const ObjectList = ({ items, sort, onSort, onSelect, nouns, picked, onPick, onPickAll, rowFilters = null, planTags = null }) => {
  const itemNoun = (nouns && nouns.item) || 'objects';
  const rows = useMemo(() => items || [], [items]);
  const headerClick = (key) => {
    if (!onSort) return;
    if (sort && sort.key === key) onSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else onSort({ key, dir: key === 'name' || key === 'prefix' || key === 'custom' ? 'asc' : 'desc' });
  };
  return (
    <div className='sfn-list'>
      <table className='metadata-itbl'>
        <thead className='metadata-sticky-head'>
          <tr>
            {picked && (
              <th className='sfn-pick' title='Choose the objects discovery samples'>
                <input type='checkbox' aria-label='choose every listed object' data-testid='sf-object-pick-all'
                  checked={rows.length > 0 && rows.every((n) => picked.has(n.table))}
                  onChange={(e) => onPickAll && onPickAll(rows.map((n) => n.table), e.target.checked)} />
              </th>
            )}
            {COLUMNS.map(([key, label, num]) => (
              <th key={key} className={num ? 'num' : undefined} role='button' tabIndex={0}
                onClick={() => headerClick(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); headerClick(key); } }}
                data-testid={`sf-object-sort-${key}`}
              >
                {label}{sort && sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
              </th>
            ))}
            {rowFilters && <th title='Rows of the object a subset pulls through this data graph'>Row filter</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={COLUMNS.length + (picked ? 1 : 0) + (rowFilters ? 1 : 0)} className='sfn-muted' style={{ textAlign: 'center', padding: 20 }}>No {itemNoun} match.</td></tr>
          )}
          {rows.map((n) => {
            const key = graphNodeKey(n);
            const label = attr(n, 'label');
            const custom = attr(n, 'custom') === true;
            const tag = planTags ? planTags[String(n.table).toLowerCase()] : null;
            return (
              <tr
                key={key}
                className='sfn-row'
                data-testid={`sf-object-row-${n.table}`}
                tabIndex={0}
                onClick={() => onSelect && onSelect(n)}
                onKeyDown={(e) => { if (e.target === e.currentTarget && e.key === 'Enter') { e.preventDefault(); if (onSelect) onSelect(n); } }}
              >
                {picked && (
                  <td className='sfn-pick' onClick={(e) => e.stopPropagation()}>
                    <input type='checkbox' aria-label={`choose ${n.table}`} data-testid={`sf-object-pick-${n.table}`}
                      checked={picked.has(n.table)} onChange={() => onPick && onPick(n.table)} />
                  </td>
                )}
                <td>
                  <span className='sfn-mono sfn-row__name'>{n.table}</span>
                  {tag && (
                    <span className={`sfn-tag sfn-plan-tag sfn-plan-tag--${tag.tone || 'info'}`} title={tag.title}
                      data-testid={`sf-object-tag-${n.table}`}>{tag.text}</span>
                  )}
                  {label && label !== n.table && <span className='sfn-sub'>{label}</span>}
                </td>
                <td>
                  <span className={`sfn-tag${custom ? ' sfn-tag--custom' : ''}`}>{custom ? 'Custom' : 'Standard'}</span>
                  {objectKindOf(n) !== 'DATA' && (
                    <span className='sfn-tag sfn-tag--kind' data-testid={`sf-object-kind-${n.table}`}>{objectKindLabel(objectKindOf(n))}</span>
                  )}
                </td>
                <td className={`num${n.rowCount == null ? ' sfn-muted' : ''}`}>{formatRecords(n.rowCount)}</td>
                <td className={`num${n.sizeBytes == null ? ' sfn-muted' : ''}`}>{formatStorage(n.sizeBytes)}</td>
                <td className='num'>{n.edgeCount ? n.edgeCount.out || 0 : 0}</td>
                <td className='num'>{n.edgeCount ? n.edgeCount.in || 0 : 0}</td>
                <td className='sfn-mono sfn-muted'>{attr(n, 'keyPrefix') || '—'}</td>
                {rowFilters && (
                  <td data-testid={`sf-object-row-filter-${n.table}`}>
                    {rowFilters[String(n.table).toLowerCase()]
                      ? <span className='sfn-tag' title={rowFilters[String(n.table).toLowerCase()]}>Filtered</span>
                      : <span className='sfn-muted'>—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ObjectList;
